import {useCallback, useState} from 'react';

import styled from 'styled-components';

import {i18n} from '@yourcoach/i18n';

import type {RequestPhoneNumberStore} from './RequestPhoneNumber.store';

import PHONE_IMAGE from '../../../../assets/phone.png';
import {alert} from '../../../../components/alert';
import {ms} from '../../../../styles';
import {Checkbox} from '../../../../uikit/Checkbox';
import {Clickable} from '../../../../uikit/Clickable';
import {Image} from '../../../../uikit/Image';
import {Text} from '../../../../uikit/Text';
import {View} from '../../../../uikit/View';
import {logger} from '../../../../utils/logger';

export const showPhoneNumberRequestAlert = ({
  isEmployee,
  onAddPhoneNumberClick,
  onDontShowAgainClick,
  place,
}: RequestPhoneNumberStore.RequestPhoneNumberAlertProps) => {
  logger.event('add_number_view', {place});

  let isDontShowAgainChecked = false;

  return alert.show({
    afterButtonsSlot: () => (
      <DontShowAgain
        onChange={value => {
          isDontShowAgainChecked = value;

          onDontShowAgainClick(value);
        }}
      />
    ),
    beforeTitleSlot: () => <S.Image src={PHONE_IMAGE} />,
    buttons: [
      {
        label: i18n.t('notifications:in_app.check_user_phone.add_button'),
        onAwaitableClick: resolve => {
          logger.event('add_number_continue_tap', {
            dont_show: isDontShowAgainChecked ? 'yes' : 'no',
            place,
          });

          onAddPhoneNumberClick();

          resolve();
        },
        scheme: 'secondary',
      },
      {
        label: i18n.t('shared:cancel'),
        onAwaitableClick: resolve => {
          logger.event('request_phone_number_close_tap', {
            dont_show: isDontShowAgainChecked ? 'yes' : 'no',
            place,
          });

          resolve();
        },
      },
    ],
    isDismissible: true,
    /**
     * @i18n_parser
     *
     * t('notifications:in_app.check_user_phone.employee.message')
     * t('notifications:in_app.check_user_phone.regular.message')
     */
    message: i18n.t(
      `notifications:in_app.check_user_phone.${isEmployee ? 'employee' : 'regular'}.message`,
    ),
    onAwaitableDismiss: resolve => {
      logger.event('request_phone_number_close_tap', {
        dont_show: isDontShowAgainChecked ? 'yes' : 'no',
        place,
      });

      resolve();
    },
    /**
     * @i18n_parser
     *
     * t('notifications:in_app.check_user_phone.employee.title')
     * t('notifications:in_app.check_user_phone.regular.title')
     */
    title: i18n.t(
      `notifications:in_app.check_user_phone.${isEmployee ? 'employee' : 'regular'}.title`,
    ),
    variant: 'promo',
  });
};

const DontShowAgain: React.FC<{onChange: (value: boolean) => void}> = ({
  onChange,
}) => {
  const [isChecked, setIsChecked] = useState(false);

  const onCheckboxClick = useCallback(() => {
    const newValue = !isChecked;

    setIsChecked(newValue);
    onChange(newValue);
  }, [isChecked, onChange]);

  return (
    <S.CheckboxContainer>
      <Checkbox
        checked={isChecked}
        onClick={onCheckboxClick}
        scheme="secondary"
      />
      <Clickable onClick={onCheckboxClick}>
        <Text>{i18n.t('shared:dont_show_again')}</Text>
      </Clickable>
    </S.CheckboxContainer>
  );
};

const S = {
  CheckboxContainer: styled(View)`
    align-items: center;
    column-gap: ${ms(8)};
    margin-block-end: ${ms(20)};
    padding-inline: ${ms(20)};
  `,
  Image: styled(Image)`
    align-self: center;
    height: ${ms(170)};
    margin-block-end: ${ms(20)};
    width: ${ms(170)};
  `,
};
